/**
 * Sign out everywhere (2026-09-28, module 1.4.0) — on /security.
 *
 * One action: every OTHER phone, tablet and computer signed in to this account on this site is
 * signed out on its next request. The backend (`signOutEverywhere`, authentication module) moves
 * `Client.sessionsValidAfter` to now and returns a fresh token for THIS device, which we adopt —
 * the old one is refused from now on.
 *
 * No "confirm it's you" step-up on purpose: signing out only takes access away, and the person
 * reaching for it may be in a hurry on the one device they still hold. It still asks once in-page.
 * Success is reported only with the time the SERVER confirmed.
 */
import React from "react";
import { Box, Button, Heading, HStack, Stack, Text, useToast } from "@chakra-ui/react";
import { FiCheckCircle, FiLogOut } from "react-icons/fi";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import type { Mutation, MutationSignOutEverywhereArgs, TwoFactorStatus } from "../../../generated/graphql";
import { MY_SESSIONS, MY_TWO_FACTOR_STATUS, SIGN_OUT_EVERYWHERE } from "../queries";
import { errorWords, signedOutAtWords } from "../utils/assurance";
import { AuthSurface, AuthTip, alpha, blurClick, useAuthConfirm, useAuthTheme } from "./ui";

export const SIGN_OUT_EVERYWHERE_TIP =
    "Sign out of every other device.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. Every other phone, tablet and computer signed in to your account on this site is signed out straight away\n  3. This device stays signed in\n\nThis changes your account: anyone on those devices has to sign in again.";

export const SignOutEverywhereCard: React.FC = () => {
    const t = useAuthTheme();
    const toast = useToast();
    const auth = useAuth();
    const navigate = useNavigate();
    const { confirm, confirmDialog } = useAuthConfirm();
    const apollo = useApolloClient();
    // Same document as the Two-step verification card, so one cache entry serves both.
    const { data, loading, error, refetch } = useQuery<{ myTwoFactorStatus: TwoFactorStatus }>(MY_TWO_FACTOR_STATUS, { fetchPolicy: "cache-and-network" });
    const [signOutMut] = useMutation<Pick<Mutation, "signOutEverywhere">, MutationSignOutEverywhereArgs>(SIGN_OUT_EVERYWHERE);
    const [busy, setBusy] = React.useState(false);
    const [confirmedAt, setConfirmedAt] = React.useState<string | null>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    // The confirm dialog hands focus back to the button when it closes, and a focused control
    // re-opens its tooltip — so let go of focus once the dialog is done (Polish rule 47).
    const releaseFocus = () => window.setTimeout(() => buttonRef.current?.blur(), 60);

    const lastCutoff = data?.myTwoFactorStatus?.sessionsValidAfter || null;

    const run = async () => {
        const ok = await confirm({
            title: "Sign out of every other device?",
            body: "Every other phone, tablet and computer signed in to your account on this site is signed out straight away and has to sign in again. This device stays signed in.",
            confirmLabel: "Sign out everywhere else",
            tone: "warning",
        });
        releaseFocus();
        if (!ok) return;
        setBusy(true);
        try {
            const res = await signOutMut({ variables: { keepThisDevice: true } });
            const out = res.data?.signOutEverywhere;
            if (!out?.signedOutAt) throw new Error("The server didn't confirm it. Your other devices may still be signed in — please try again.");
            if (!out.token) {
                // The server signed this device out too — say so, then go to sign-in.
                toast({ title: "Signed out everywhere", description: "Including this device. Please sign in again.", status: "info", duration: 6000 });
                auth.logout();
                navigate("/login");
                return;
            }
            if (auth.adoptToken) await auth.adoptToken(out.token);
            else { localStorage.setItem("auth_token", out.token); await auth.refreshAuth?.(); }
            setConfirmedAt(out.signedOutAt);
            toast({ title: `Signed out of every other device at ${signedOutAtWords(out.signedOutAt)}`, status: "success", duration: 5000 });
            await refetch().catch(() => undefined);
            // "Where you're signed in" (1.5.0) shows the other devices gone — re-read it if it's on screen.
            const sessionsOnScreen = Array.from(apollo.getObservableQueries().values()).some((q) => q.queryName === "MySessions");
            if (sessionsOnScreen) {
                await apollo.refetchQueries({ include: [MY_SESSIONS] }).catch(() => undefined);
            }
        } catch (err) {
            toast({ title: "Couldn't sign out your other devices", description: errorWords(err), status: "error", duration: 8000 });
        } finally {
            setBusy(false);
            releaseFocus();
        }
    };

    return (
        <AuthSurface data-testid="sign-out-everywhere-card">
            <HStack spacing={3} mb={3}>
                <Box as={FiLogOut} color={t.text} aria-hidden />
                <Heading size="sm" color={t.text}>Sign out everywhere</Heading>
            </HStack>
            <Stack spacing={4}>
                <Text fontSize="md" color={t.muted}>
                    Signs you out on every other phone, tablet and computer where you're signed in to this site — for
                    example a lost phone or a computer you shared. You stay signed in here. Changing your password does
                    this too.
                </Text>

                {confirmedAt ? (
                    <HStack
                        role="status"
                        align="start"
                        spacing={2}
                        p={3}
                        borderRadius="lg"
                        bg={alpha(t.success, 0.1)}
                        borderWidth="1px"
                        borderColor={alpha(t.success, 0.35)}
                        data-testid="sign-out-everywhere-confirmed"
                    >
                        <Box as={FiCheckCircle} color={t.success} mt="3px" flexShrink={0} aria-hidden />
                        <Text fontSize="md" color={t.text}>
                            Signed out of every other device at {signedOutAtWords(confirmedAt)}. This device is still signed in.
                        </Text>
                    </HStack>
                ) : loading && !data ? (
                    <Text fontSize="md" color={t.faint} data-testid="sign-out-everywhere-checking">Checking when your devices were last signed out…</Text>
                ) : error && !data ? (
                    <Text fontSize="md" color={t.muted} data-testid="sign-out-everywhere-unknown">
                        Couldn't check when your devices were last signed out. You can still sign out everywhere.
                    </Text>
                ) : lastCutoff ? (
                    <Text fontSize="md" color={t.muted} data-testid="sign-out-everywhere-last">
                        Sign-ins from before {signedOutAtWords(lastCutoff)} no longer work.
                    </Text>
                ) : null}

                <AuthTip label={SIGN_OUT_EVERYWHERE_TIP}>
                    <Button
                        ref={buttonRef}
                        size="lg"
                        minH="48px"
                        w={{ base: "full", sm: "auto" }}
                        colorScheme="blue"
                        leftIcon={<FiLogOut />}
                        isLoading={busy}
                        loadingText="Signing out other devices…"
                        onClick={blurClick(() => void run())}
                        data-testid="sign-out-everywhere-button"
                    >
                        Sign out everywhere else
                    </Button>
                </AuthTip>
            </Stack>
            {confirmDialog}
        </AuthSurface>
    );
};

export default SignOutEverywhereCard;
